import { Injectable } from '@angular/core';
import {
  parseEstablishmentId,
  parseStoredPermissionTree,
  serializePermissionSource,
} from '../../common-files/permission-tree';

// Handles the restructured login permissions: { global, establishments }, where
// each bucket groups views by role and every permission carries an expiry.
//
// The operator acts in ONE scope at a time (global OR one establishment). The
// chosen bucket is stored raw in `permissions`, retaining validUntil metadata so
// PermissionService can re-evaluate expiry every time permissions are read.
@Injectable({ providedIn: 'root' })
export class ContextService {
  // -- read the raw scoped permissions saved at login --------------------
  private getScoped(): any {
    const stored = localStorage.getItem('permissionsScoped');
    if (!stored) return { global: { roleViews: [] }, establishments: [] };
    try {
      return JSON.parse(stored);
    } catch {
      return { global: { roleViews: [] }, establishments: [] };
    }
  }

  hasGlobal(): boolean {
    const roleViews = this.getScoped().global?.roleViews;
    return Array.isArray(roleViews) && roleViews.length > 0;
  }

  // the global bucket's role entries, [{ role: {roleName,...}, views: [...] }]
  globalRoleViews(): any[] {
    const roleViews = this.getScoped().global?.roleViews;
    return Array.isArray(roleViews) ? roleViews : [];
  }

  establishmentBuckets(): any[] {
    const establishments = this.getScoped().establishments;
    return Array.isArray(establishments) ? establishments : [];
  }

  // true when the operator has both global and establishment access -> must pick
  // a scope first.
  needsScopeChoice(): boolean {
    return this.hasGlobal() && this.establishmentBuckets().length > 0;
  }

  // true when the establishment scope has more than one establishment -> must pick
  // which establishment.
  needsEstablishmentChoice(): boolean {
    return this.establishmentBuckets().length > 1;
  }

  // -- apply a chosen scope: compute the flat menu + persist the context --
  applyGlobal(): void {
    const scoped = this.getScoped();
    const global = scoped.global || { roleViews: [] };
    const roleViews = Array.isArray(global.roleViews) ? global.roleViews : [];
    localStorage.setItem('activeScope', 'GLOBAL');
    localStorage.removeItem('activeEstablishmentId');
    localStorage.removeItem('activeEstablishmentName');
    this.clearPropertyContext();
    localStorage.setItem('roleName', this.roleNamesOf(roleViews));
    localStorage.setItem('permissions', serializePermissionSource({
      permissions: {
        global,
        establishments: [],
      },
    }));
  }

  applyEstablishment(establishmentId: number): boolean {
    const bucket = this.establishmentBuckets().find(
      (b: any) => Number(b.establishmentId) === Number(establishmentId)
    );
    if (!bucket) return false;

    const roleViews = Array.isArray(bucket.roleViews) ? bucket.roleViews : [];
    localStorage.setItem('activeScope', 'ESTABLISHMENT');
    localStorage.setItem('activeEstablishmentId', String(establishmentId));
    localStorage.setItem('activeEstablishmentName', bucket?.estName || '');
    this.clearPropertyContext();
    localStorage.setItem('roleName', this.roleNamesOf(roleViews));
    localStorage.setItem('permissions', serializePermissionSource({
      permissions: {
        global: { roleViews: [] },
        establishments: [bucket],
      },
    }));
    return true;
  }

  // pick the obvious context when no prompt is needed: the single establishment,
  // else global.
  applyDefault(): void {
    const buckets = this.establishmentBuckets();
    if (!this.hasGlobal() && buckets.length === 1) {
      this.applyEstablishment(Number(buckets[0].establishmentId));
    } else {
      this.applyGlobal();
    }
  }

  // first navigable route in the applied menu — where to land after selection.
  firstRoute(): string | null {
    const tree = parseStoredPermissionTree(
      localStorage.getItem('permissions'),
      parseEstablishmentId(localStorage.getItem('activeEstablishmentId')),
    );
    const dfs = (nodes: any[]): string | null => {
      for (const n of nodes || []) {
        if (n?.webRoute) return n.webRoute;
        const child = dfs(n?.subview);
        if (child) return child;
      }
      return null;
    };
    return dfs(tree);
  }

  // -- helpers -----------------------------------------------------------

  // the role names contributing to a bucket, for the "role" line in the sidebar.
  private roleNamesOf(roleViews: any[]): string {
    const names = (roleViews || [])
      .map((rv: any) => rv?.role?.roleName)
      .filter((n: any) => !!n);
    return [...new Set(names)].join(', ');
  }

  private clearPropertyContext(): void {
    localStorage.removeItem('activePropertyId');
    localStorage.removeItem('activePropertyName');
  }
}
